/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 17-Jul-2026
 * @author      : NAOMI Mapping
 * Requirement# : 22300
 * Purpose      : Trade Templates page — loads the library model and wires the API persist seam.
 */

import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useOidcAccessToken } from '@axa-fr/react-oidc';

import TradeTemplateSystem, { TradeTemplatePersist, BuilderCtx } from '../naomi/tradeTemplates/TradeTemplateSystem';
import { useTradeTemplateData } from '../naomi/tradeTemplates/useTradeTemplateData';
import {
  saveTradeTemplate, saveDraft, publishDraft, makeCurrent, promote,
  listVersions as fetchVersions, mapApiVersions, resolveCurrentTemplate, flattenEntriesToTasks,
  serializeTasksForStorage, getDraft, SourceLineage,
} from '../naomi/tradeTemplates/services/TradeTemplateVersionService';

/** Source lineage of the scope currently being edited — recorded on any copy saved into a DIFFERENT
 *  scope/tenant, so we can trace "saved from tenant X / provider / global". */
function sourceOf(ctx: BuilderCtx): SourceLineage {
  const m = /^v(\d+)$/.exec(ctx.version || '');
  return { sourceScope: ctx.scope, sourceVersion: m ? Number(m[1]) : null, sourceTenantId: ctx.tenantId };
}

/**
 * Scoped theme for the Trade Templates section. The design was built against a clean MUI theme, so we
 * deliberately DON'T inherit provider-web's global theme here (Poppins, fontSize:13, brand overrides)
 * which flattens the intended look. A neutral system font + default MUI sizing + a soft grey canvas
 * reproduce the handoff design; the GMS blue is kept for brand-consistent primary accents.
 */
const tradeTemplatesTheme = createTheme({
  palette: {
    primary: { main: '#3c5bfa' },
    background: { default: '#f6f7f9', paper: '#ffffff' },
  },
  typography: {
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    fontSize: 14,
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: { defaultProps: { disableElevation: true }, styleOverrides: { root: { textTransform: 'none' } } },
  },
});

const TradeTemplates: React.FC = () => {
  const { accessToken, accessTokenPayload } = useOidcAccessToken();
  const oid = String(accessTokenPayload?.oid ?? '');
  const updatedBy = String(accessTokenPayload?.emails?.[0] ?? accessTokenPayload?.oid ?? 'PROVIDER');

  const { trades, tenants, globalDefault, loading, error, reload, loadTenantVersions } = useTradeTemplateData(accessToken);

  const nameForTrade = (tradeId: string | null): string | undefined =>
    (tradeId ? trades.find(t => t.id === tradeId)?.name : undefined);

  const persist: TradeTemplatePersist = {
    saveNewVersion: async (ctx, tasks, targets) => {
      const tradeId = Number(ctx.tradeId);
      const tasksJson = serializeTasksForStorage(tasks, ctx.aseVersion);
      const name = nameForTrade(ctx.tradeId);
      // Save destinations are only ever "this tenant" and/or "the provider" — templates are never
      // shared between tenants, so the current tenant is the sole tenant target UNLESS the modal
      // offered (and the user picked) specific tenants — only possible while editing a trade's Provider
      // default — in which case exactly the tenants the user chose (all or a subset) are targeted. This
      // is a provider->many-tenants publish, not tenant-to-tenant sharing.
      const tenantTargets = targets.tenantIds.length
        ? targets.tenantIds
        : (targets.toCurrentTenant && ctx.tenantId ? [ctx.tenantId] : []);

      // Guard against a silent no-op that would still toast success: e.g. on provider/global scope the
      // "current tenant" checkbox has no tenant to resolve, so with provider unchecked nothing is written.
      if (!targets.toProvider && tenantTargets.length === 0) {
        throw new Error('No save destination — pick the provider default' + (ctx.tenantId ? ' or this tenant.' : '.'));
      }

      if (targets.toProvider) {
        // Promoting to the provider default from a tenant/global records source lineage on the master
        // (not when editing the provider itself, which is just its own new version).
        const providerSrc: SourceLineage = ctx.scope !== 'provider' ? sourceOf(ctx) : {};
        const r = await saveTradeTemplate(tradeId, { name, tasks: tasksJson, tenantIds: tenantTargets, updatedBy, note: targets.note, ...providerSrc }, accessToken);
        if (!r.ok) throw new Error(r.message ?? 'Save failed');
      } else {
        for (const tid of tenantTargets) {
          // The current tenant only — a new version in its own history (no cross-scope lineage).
          const d = await saveDraft(tradeId, { name, tasks: tasksJson, scope: 'tenant', tenantId: tid, updatedBy }, accessToken);
          if (!d.ok) throw new Error(d.message ?? 'Draft save failed');
          const p = await publishDraft(tradeId, { scope: 'tenant', tenantId: tid, updatedBy, note: targets.note }, accessToken);
          if (!p.ok) throw new Error(p.message ?? 'Publish failed');
        }
      }
    },

    saveDraft: async (ctx, tasks) => {
      if (ctx.scope === 'global') throw new Error('The Global Default is not draft-editable here.');
      const r = await saveDraft(Number(ctx.tradeId), {
        name: nameForTrade(ctx.tradeId), tasks: serializeTasksForStorage(tasks, ctx.aseVersion), scope: ctx.scope, tenantId: ctx.tenantId, updatedBy,
      }, accessToken);
      if (!r.ok) throw new Error(r.message ?? 'Draft save failed');
    },

    makeCurrent: async (ctx, _version, versionGroupUid) => {
      if (!versionGroupUid) throw new Error('This version cannot be activated (missing version group id).');
      const r = await makeCurrent(Number(ctx.tradeId), { versionGroupUid, scope: ctx.scope, tenantId: ctx.tenantId, updatedBy }, accessToken);
      if (!r.ok) throw new Error(r.message ?? 'Make current failed');
    },

    promote: async (trade, tenantId) => {
      const r = await promote(Number(trade.id), { sourceTenantId: tenantId, sourceVersionGroupUid: null, updatedBy }, accessToken);
      if (!r.ok) throw new Error(r.message ?? 'Promote failed');
    },

    listVersions: async (scope, tradeId, tenantId) => {
      const id = tradeId ? Number(tradeId) : 0; // tradeId is ignored for the global scope
      const r = await fetchVersions(id, scope, tenantId, accessToken);
      if (!r.ok) throw new Error(r.message ?? 'Failed to load versions');
      return mapApiVersions(r.data?.versions);
    },

    resolveCurrent: async (tradeId) => {
      const r = await resolveCurrentTemplate(Number(tradeId), accessToken);
      if (!r.ok || !r.data) return { source: 'none', tasks: [] };
      return { source: r.data.source, tasks: flattenEntriesToTasks(r.data.entries) };
    },

    loadDraft: async (scope, tradeId, tenantId) => {
      if (!tradeId) return { hasDraft: false, tasks: [], when: null, forkedFrom: null };
      const r = await getDraft(Number(tradeId), scope, tenantId, accessToken);
      if (!r.ok || !r.data?.hasDraft) return { hasDraft: false, tasks: [], when: null, forkedFrom: null };
      const ff = r.data.forkedFrom;
      return {
        hasDraft: true,
        tasks: flattenEntriesToTasks(r.data.entries),
        when: r.data.updatedAt ?? null,
        forkedFrom: ff
          ? { scope: ff.scope as 'provider' | 'global' | 'tenant', version: ff.version != null ? `v${ff.version}` : '', tenantId: ff.tenantId ?? undefined }
          : null,
      };
    },
  };

  return (
    <ThemeProvider theme={tradeTemplatesTheme}>
      <Box sx={{ height: 'calc(100vh - 64px)', overflow: 'hidden', bgcolor: 'background.paper' }}>
        {loading && (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>
        )}
        {!loading && error && (
          <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>
        )}
        {!loading && !error && (
          <TradeTemplateSystem trades={trades} tenants={tenants} globalDefault={globalDefault}
            oid={oid} persist={persist} onReload={reload} onSelectTenant={loadTenantVersions} />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default TradeTemplates;
